/**
 * Minimal device-tier system (rendering plan §2.1/§4 — Phase R1 slice). Three tiers drive
 * texture/shadow/post budgets; in R1 only the readout + the render-buffer type consume it.
 *
 *   tier 2 — WebGPU backend on a desktop-ish UA (dGPU assumed until measured)
 *   tier 1 — WebGPU backend elsewhere (tablets/phones with WebGPU)
 *   tier 0 — WebGL2 fallback backend (the shipped baseline path, D2.2)
 *
 * `?tier=<0|1|2>` overrides detection (device testing knob). Pure logic (override/UA parsing)
 * is exported separately so it unit-tests headlessly.
 */

export type Tier = 0 | 1 | 2;

/**
 * Default `texture.anisotropy` per tier (plan §2.1) — exported for the future texture loader
 * (A4/R2). three's default is 1; UE's "free" aniso must be opted into explicitly here.
 */
export const TIER_ANISOTROPY: Readonly<Record<Tier, number>> = { 0: 2, 1: 4, 2: 8 };

/** Parse a raw `?tier=` query-param value. Anything but '0'|'1'|'2' → null (no override). */
export function parseTierOverride(value: string | null | undefined): Tier | null {
  if (value === '0') return 0;
  if (value === '1') return 1;
  if (value === '2') return 2;
  return null;
}

/**
 * Desktop-ish UA heuristic: not a phone/tablet token. Deliberately coarse — the tier override
 * and the (R2) real device sweep are the precision tools; this only splits desktop WebGPU from
 * mobile WebGPU for the tier-2 default.
 */
export function isDesktopUA(ua: string): boolean {
  return !/Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
}

/** The renderer surface detectTier needs — matches WebGPURenderer, fabricable in tests. */
export interface TierRendererLike {
  backend?: { isWebGPUBackend?: boolean };
}

/**
 * Detect the device tier. Call after `renderer.init()` (the backend is decided there);
 * `override` is the raw `?tier=` query-param value and wins outright when valid.
 */
export function detectTier(
  renderer: TierRendererLike,
  override?: string | null,
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): Tier {
  const forced = parseTierOverride(override);
  if (forced !== null) return forced;
  const webgpu = renderer.backend?.isWebGPUBackend === true;
  if (webgpu && isDesktopUA(ua)) return 2;
  if (webgpu) return 1;
  return 0;
}
